import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableUserSelect from "@/components/SearchableUserSelect";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Sword, Trophy, Trash, PencilSimple, Broadcast, UserPlus, UsersThree, X, ClipboardText } from "@phosphor-icons/react";
import { CATEGORIES, categoryShort } from "@/lib/locations";
import EventMediaGallery from "@/components/EventMediaGallery";
import QuickReportButton from "@/components/QuickReportButton";
import PlanillaPorCancha from "@/components/PlanillaPorCancha";
import RetaBracket from "@/components/RetaBracket";
import WhatsAppShareButton from "@/components/WhatsAppShareButton";
import RetaScoreboard from "@/components/RetaScoreboard";
import CourtRanking from "@/components/CourtRanking";
import RoundRoster from "@/components/RoundRoster";
import RoundClosePanel from "@/components/RoundClosePanel";
import RetaGeneralRanking from "@/components/RetaGeneralRanking";
import RosterImageAssign from "@/components/RosterImageAssign";

const statusColors = {
  abierto: "bg-green-500/20 text-green-400 border-green-500/30",
  en_curso: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  finalizada: "bg-gray-500/20 text-gray-300 border-gray-500/30",
  cancelada: "bg-red-500/20 text-red-400 border-red-500/30",
  // legacy
  pendiente: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  aceptada: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  rechazada: "bg-red-500/20 text-red-400 border-red-500/30",
  completada: "bg-green-500/20 text-green-400 border-green-500/30",
};

const FORMAT_INFO = {
  standard: {
    label: "Estandar · 2 de 3 sets",
    rules: ["Mejor de 3 sets", "Set se gana con 6 juegos (margen de 2)", "En 6-6 se juega tiebreak a 7 puntos"],
    example: "6-4",
  },
  short: {
    label: "Corto · Sets a 4",
    rules: ["Mejor de 3 sets", "Set se gana con 4 juegos (margen de 2)", "En 3-3 se juega tiebreak a 7 puntos"],
    example: "4-3 TB 7-5",
  },
};

const EMPTY_CREATE = {
  club_id: "",
  date: "",
  time: "",
  entry_fee: 0,
  description: "",
  game_format: "standard",
  category: "",
  courts_text: "Cancha 1",
  pdf_blank_slots_per_court: 10,
};

export default function Retas() {
  const { user, permissions } = useAuth();
  const navigate = useNavigate();
  const isAdminOrDir = user && ["admin", "directivo"].includes(user.role);
  const canCreateRetas = permissions?.canCreateRetas;
  const canSeePlanilla = user && ["admin", "directivo", "profesor", "desarrollador"].includes(user.role);
  // Returns true if current user can edit/delete a specific reta (admin, directivo of club, or creator)
  const canManageReta = (r) => {
    if (!user || !r) return false;
    if (user.role === "admin") return true;
    if (user.role === "directivo" && r.club_id && r.club_id === user.club_id) return true;
    if (r.created_by && r.created_by === user.id) return true;
    return false;
  };

  const [retas, setRetas] = useState([]);
  const [planillaOpen, setPlanillaOpen] = useState(null); // {retaId, courtIdx, courtName, registeredUsers}
  const [bracketOpen, setBracketOpen] = useState(null); // {retaId, retaName}
  const [players, setPlayers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);

  const [assignDialog, setAssignDialog] = useState(null); // {reta, courtIdx}
  const [assignForm, setAssignForm] = useState({ user_id: "", name: "", is_guest: false });
  const [assignMode, setAssignMode] = useState("manual"); // "manual" | "image"

  const [editDialog, setEditDialog] = useState(null);
  const [editForm, setEditForm] = useState({ date: "", time: "", entry_fee: 0, description: "", game_format: "standard", status: "abierto", pdf_blank_slots_per_court: 10 });

  // Active scoreboard tab per court → key = `${retaId}:${idx}` → sb_id (string) | null (closed)
  const [activeSb, setActiveSb] = useState({});
  const setActiveSbFor = (retaId, idx, sbId) => {
    setActiveSb((prev) => ({ ...prev, [`${retaId}:${idx}`]: sbId }));
  };

  const createNewScoreboard = async (retaId, idx, roundNum = null) => {
    try {
      const payload = roundNum !== null ? { round: roundNum } : {};
      const res = await api.post(`/retas/multi/${retaId}/courts/${idx}/scoreboards`, payload);
      const sb = res.data.scoreboard;
      toast.success(`Ronda ${sb.round} · Reta #${sb.number} creada`);
      setActiveSbFor(retaId, idx, sb.id);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear pizarron");
    }
  };

  const createNewRound = async (retaId, idx, currentMaxRound) => {
    const nextRound = (currentMaxRound || 0) + 1;
    await createNewScoreboard(retaId, idx, nextRound);
  };

  const deleteScoreboard = async (retaId, idx, sbId, number) => {
    if (!window.confirm(`Eliminar Reta #${number} de esta cancha?`)) return;
    try {
      await api.delete(`/retas/multi/${retaId}/courts/${idx}/scoreboards/${sbId}`);
      toast.success("Pizarron eliminado");
      setActiveSb((prev) => { const n = { ...prev }; if (n[`${retaId}:${idx}`] === sbId) n[`${retaId}:${idx}`] = null; return n; });
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar");
    }
  };

  const loadAll = useCallback(() => {
    Promise.all([api.get("/retas"), api.get("/users?role=jugador"), api.get("/clubs")])
      .then(([r, p, c]) => { setRetas(r.data || []); setPlayers(p.data || []); setClubs(c.data || []); })
      .catch(() => toast.error("Error al cargar"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadRetas = () => api.get("/retas").then(({ data }) => setRetas(data));

  // Split retas
  const { multiRetas, legacyRetas } = useMemo(() => {
    const multi = [], legacy = [];
    for (const r of retas) {
      if (r.kind === "multi") multi.push(r);
      else legacy.push(r);
    }
    return { multiRetas: multi, legacyRetas: legacy };
  }, [retas]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const courts = createForm.courts_text.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
    if (!courts.length) { toast.error("Define al menos 1 cancha"); return; }
    try {
      await api.post("/retas/multi", {
        club_id: createForm.club_id,
        date: createForm.date,
        time: createForm.time,
        entry_fee: parseFloat(createForm.entry_fee) || 0,
        description: createForm.description,
        game_format: createForm.game_format,
        category: createForm.category || null,
        courts,
        max_per_court: 32,
        pdf_blank_slots_per_court: Number.isFinite(parseInt(createForm.pdf_blank_slots_per_court, 10))
          ? Math.max(0, Math.min(50, parseInt(createForm.pdf_blank_slots_per_court, 10)))
          : 10,
      });
      toast.success("Reta creada");
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE);
      loadRetas();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const enroll = async (id) => {
    try { await api.post(`/retas/multi/${id}/enroll`); toast.success("Inscrito"); loadRetas(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const unenroll = async (id) => {
    try { await api.post(`/retas/multi/${id}/unenroll`); toast.success("Inscripcion cancelada"); loadRetas(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const openAssign = (reta, courtIdx) => {
    setAssignForm({ user_id: "", name: "", is_guest: false });
    setAssignMode("manual");
    setAssignDialog({ reta, courtIdx });
  };

  // Fire a lime-themed confetti burst when a court just filled all 4 slots.
  const _celebrateFull = useCallback((courtName) => {
    const colors = ["#CCFF00", "#00FF88", "#FFFFFF", "#00A0FF", "#FFD700"];
    try {
      confetti({ particleCount: 120, spread: 85, origin: { y: 0.4 }, colors, scalar: 1.1 });
      setTimeout(() => confetti({ particleCount: 60, spread: 110, angle: 60,  origin: { x: 0,  y: 0.6 }, colors }), 180);
      setTimeout(() => confetti({ particleCount: 60, spread: 110, angle: 120, origin: { x: 1,  y: 0.6 }, colors }), 320);
    } catch { /* no-op */ }
    try { toast.success(`¡${courtName} completa! 4 jugadores confirmados 🎉`); } catch {}
  }, []);

  const handleAssign = async () => {
    if (!assignDialog) return;
    const { reta, courtIdx } = assignDialog;
    const payload = assignForm.user_id
      ? { user_id: assignForm.user_id, name: assignForm.name, is_guest: false }
      : { name: assignForm.name, is_guest: true };
    if (!payload.name) { toast.error("Nombre requerido"); return; }
    // Snapshot before mutation so we know if this assignment fills the court
    const beforeCount = (reta.courts?.[courtIdx]?.lineup || []).filter(Boolean).length;
    try {
      await api.post(`/retas/multi/${reta.id}/courts/${courtIdx}/assign`, payload);
      toast.success("Jugador asignado");
      setAssignDialog(null);
      await loadRetas();
      if (beforeCount === 3) {
        _celebrateFull(reta.courts?.[courtIdx]?.name || "Cancha");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleRemovePlayer = async (reta, courtIdx, player) => {
    if (!window.confirm(`Remover ${player.name} de ${reta.courts[courtIdx].name}?`)) return;
    try {
      await api.post(`/retas/multi/${reta.id}/courts/${courtIdx}/remove`, player.user_id ? { user_id: player.user_id } : { name: player.name, is_guest: true });
      toast.success("Jugador removido");
      loadRetas();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const openEdit = (r) => {
    setEditForm({
      date: r.date || "",
      time: r.time || "",
      entry_fee: r.entry_fee || 0,
      description: r.description || "",
      game_format: r.game_format || "standard",
      status: r.status || "abierto",
      pdf_blank_slots_per_court: Number.isFinite(parseInt(r.pdf_blank_slots_per_court, 10)) ? r.pdf_blank_slots_per_court : 10,
    });
    setEditDialog(r);
  };
  const handleEdit = async () => {
    if (!editDialog) return;
    try {
      await api.put(`/retas/multi/${editDialog.id}`, {
        ...editForm,
        entry_fee: parseFloat(editForm.entry_fee) || 0,
        pdf_blank_slots_per_court: Math.max(0, Math.min(50, parseInt(editForm.pdf_blank_slots_per_court, 10) || 10)),
      });
      toast.success("Reta modificada");
      setEditDialog(null);
      loadRetas();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar esta reta?")) return;
    try { await api.delete(`/admin/retas/${id}`); toast.success("Reta eliminada"); loadRetas(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const launchScoreboard = async (reta, courtIdx) => {
    const c = reta.courts[courtIdx];
    const pl = c.players || [];
    if (pl.length < 4) { toast.error(`Se necesitan 4 jugadores en ${c.name} (actualmente ${pl.length})`); return; }
    try {
      const { data } = await api.post("/matches", {
        pair1_p1: { id: pl[0].user_id, name: pl[0].name },
        pair1_p2: { id: pl[1].user_id, name: pl[1].name },
        pair2_p1: { id: pl[2].user_id, name: pl[2].name },
        pair2_p2: { id: pl[3].user_id, name: pl[3].name },
        reta_id: reta.id,
        club_id: reta.club_id || null,
        category: reta.category || null,
        date: reta.date,
        time: reta.time || "",
        format: reta.game_format || "standard",
      });
      toast.success("Marcador iniciado");
      navigate(`/dashboard/marcador/${data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="retas-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-1">Desafios grupales</p>
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl font-black uppercase tracking-tighter">Retas</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canCreateRetas && (
            <Button onClick={() => { setCreateForm(EMPTY_CREATE); setCreateOpen(true); }} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs min-h-[44px]" data-testid="create-reta-btn">
              <Plus size={16} className="mr-2" /> Nueva Reta
            </Button>
          )}
          <QuickReportButton section="retas" variant="compact" />
        </div>
      </div>

      {/* FORMAT RULES CARD */}
      <div className="grid md:grid-cols-2 gap-3">
        {Object.entries(FORMAT_INFO).map(([k, v]) => (
          <div key={k} className="card-tactical p-4">
            <div className="text-[11px] tracking-[0.2em] uppercase text-[#CCFF00] font-bold mb-1">{v.label}</div>
            <ul className="text-xs text-gray-400 space-y-1 mb-2">
              {v.rules.map((ru, i) => <li key={i}>· {ru}</li>)}
            </ul>
            <div className="text-[10px] text-gray-500">Ejemplo: <span className="text-white font-mono">{v.example}</span></div>
          </div>
        ))}
      </div>

      {/* MULTI RETAS */}
      <div className="space-y-4">
        <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400">Activas</h2>
        {multiRetas.length === 0 ? (
          <div className="card-tactical p-10 text-center">
            <UsersThree size={40} weight="duotone" className="text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No hay retas activas</p>
          </div>
        ) : multiRetas.map((r) => {
          const club = clubs.find((c) => c.id === r.club_id);
          const totalAssigned = (r.courts || []).reduce((a, c) => a + (c.players?.length || 0), 0);
          const enrolled = (r.enrolled || []).length;
          const isEnrolled = user && (r.enrolled || []).includes(user.id);
          const isAssignedSomewhere = (r.courts || []).some((c) => (c.players || []).some((p) => p.user_id === user?.id));
          return (
            <div key={r.id} className="card-tactical p-5" data-testid={`reta-multi-${r.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge variant="outline" className={`badge-status ${statusColors[r.status] || ""}`}>{r.status}</Badge>
                    <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[10px]">
                      {FORMAT_INFO[r.game_format]?.label || r.game_format}
                    </Badge>
                    {r.category && <Badge variant="outline" className="bg-white/5 border-white/10 text-[10px]">Cat. {categoryShort(r.category)}</Badge>}
                  </div>
                  <div className="flex gap-3 text-xs text-gray-400 flex-wrap">
                    {club && <span>🏢 {club.name}</span>}
                    <span>📅 {r.date}{r.time ? ` · ⏰ ${r.time}` : ""}</span>
                    <span className="text-[#CCFF00]">Cuota: {r.entry_fee > 0 ? `$${r.entry_fee}` : "Gratis"}</span>
                    <span>Inscritos: <span className="text-white font-bold">{enrolled + totalAssigned}</span></span>
                  </div>
                  {r.description && <p className="text-xs text-gray-500 mt-2">"{r.description}"</p>}
                </div>
                <div className="flex gap-2 flex-wrap w-full sm:w-auto sm:shrink-0">
                  {!isAdminOrDir && !isEnrolled && !isAssignedSomewhere && r.status === "abierto" && (
                    <Button size="sm" onClick={() => enroll(r.id)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] text-xs uppercase font-bold min-h-[40px]" data-testid={`enroll-reta-${r.id}`}>
                      <UserPlus size={14} className="mr-1" /> Inscribirme
                    </Button>
                  )}
                  {!isAdminOrDir && isEnrolled && (
                    <Button size="sm" onClick={() => unenroll(r.id)} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs uppercase font-bold min-h-[40px]" data-testid={`unenroll-reta-${r.id}`}>
                      <X size={14} className="mr-1" /> Cancelar inscripcion
                    </Button>
                  )}
                  {canManageReta(r) && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => openEdit(r)} className="border-white/10 text-xs uppercase font-bold min-h-[40px]" data-testid={`edit-reta-${r.id}`}>
                        <PencilSimple size={14} className="mr-1" /> Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(r.id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs uppercase font-bold min-h-[40px] min-w-[40px]" data-testid={`delete-reta-${r.id}`}>
                        <Trash size={14} />
                      </Button>
                    </>
                  )}{canSeePlanilla && (
                    <Button size="sm" onClick={() => setBracketOpen({ retaId: r.id, retaName: club ? club.name : "Reta" })} className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 hover:from-amber-500/30 hover:to-orange-500/30 text-amber-200 border border-amber-500/40 text-xs uppercase font-bold min-h-[40px]" data-testid={`bracket-btn-${r.id}`}>
                      <Trophy size={14} className="mr-1" /> Bracket
                    </Button>
                  )}
                  <EventMediaGallery entityType="retas" entityId={r.id} testIdPrefix={`reta-media-${r.id}`} />
                  <QuickReportButton section="retas" params={{ reta_id: r.id }} variant="compact" />
                  <WhatsAppShareButton
                    message={`Reta Disfruta y Diviértete\n${club ? `Club: ${club.name}\n` : ""}Fecha: ${r.date}${r.time ? ` · ${r.time}` : ""}\nFormato: ${FORMAT_INFO[r.game_format]?.label || r.game_format}\nCuota: ${r.entry_fee > 0 ? `$${r.entry_fee}` : "Gratis"}\nInscritos: ${enrolled + totalAssigned}\n${r.description ? `\n"${r.description}"\n` : ""}¡Inscríbete en la app!`}
                    testId={`whatsapp-reta-${r.id}`}
                  />
                </div>
              </div>

              {/* Enrolled (pending assign) — admin/directivo/creador */}
              {canManageReta(r) && enrolled > 0 && (
                <div className="mb-3 p-3 bg-[#0A0A0A] border border-white/5">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500 mb-2">Por asignar ({enrolled})</div>
                  <div className="flex flex-wrap gap-2">
                    {(r.enrolled || []).map((uid) => {
                      const u = players.find((p) => p.id === uid);
                      return <span key={uid} className="px-2 py-1 bg-white/5 text-xs text-white">{u?.name || uid.slice(0, 8)}</span>;
                    })}
                  </div>
                </div>
              )}

              {/* COURTS */}
              <div className="grid sm:grid-cols-2 gap-3">
                {(r.courts || []).map((c, idx) => {
                  // Normalize scoreboards list (with legacy migration)
                  let sbs = Array.isArray(c.scoreboards) ? c.scoreboards.slice() : [];
                  if (!sbs.length && c.scoreboard) {
                    sbs = [{ ...c.scoreboard, id: c.scoreboard.id || `legacy-${idx}`, number: 1 }];
                  }
                  sbs.sort((a, b) => (a.number || 0) - (b.number || 0));
                  const activeKey = `${r.id}:${idx}`;
                  const activeId = activeSb[activeKey];
                  const active = sbs.find((s) => s.id === activeId);
                  const isAssignedHere = (c.players || []).some((p) => p.user_id === user?.id);
                  const canEditSb = canManageReta(r) || isAssignedHere;
                  return (
                  <div key={idx} className="bg-[#0A0A0A] border border-white/10 p-3" data-testid={`reta-court-${r.id}-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-bold text-white flex items-center gap-2">
                        {c.name}
                        {sbs.length > 0 && (
                          <span className="px-1.5 py-0.5 bg-[#CCFF00]/15 text-[#CCFF00] text-[9px] font-mono rounded border border-[#CCFF00]/30" data-testid={`reta-court-${r.id}-${idx}-sb-count`}>
                            {sbs.length} reta{sbs.length !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-gray-500">{(c.players || []).length} / {c.capacity}</div>
                    </div>
                    <div className="flex gap-2 mb-2 flex-wrap">
                      {canManageReta(r) && (
                        <Button size="sm" variant="outline" onClick={() => openAssign(r, idx)} className="border-[#CCFF00]/30 text-[#CCFF00] hover:bg-[#CCFF00]/10 text-[10px] uppercase h-7" data-testid={`assign-reta-${r.id}-${idx}`}>
                          <UserPlus size={12} className="mr-1" /> Asignar
                        </Button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {(c.players || []).length === 0 ? (
                        <span className="text-[10px] text-gray-600">Sin jugadores</span>
                      ) : (c.players || []).map((p, pi) => (
                        <span key={pi} className={`px-2 py-1 text-[10px] flex items-center gap-1 ${p.is_guest ? "bg-yellow-500/10 text-yellow-300 border border-yellow-500/20" : "bg-white/5 text-white border border-white/10"}`}>
                          {p.name}{p.is_guest ? " (inv)" : ""}
                          {canManageReta(r) && (
                            <button onClick={() => handleRemovePlayer(r, idx, p)} className="hover:text-red-400"><X size={10} /></button>
                          )}
                        </span>
                      ))}
                    </div>

                    {/* Scoreboards tabs — grouped by round */}
                    <div className="pt-2 border-t border-white/5">
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <div className="text-[10px] tracking-[0.2em] uppercase text-gray-400 font-bold">Pizarrones por ronda</div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {canSeePlanilla && (
                            <Button size="sm" onClick={() => setPlanillaOpen({ retaId: r.id, courtIdx: idx, courtName: c.name, registeredUsers: (c.players || []).map((p) => ({ id: p.user_id, name: p.name, is_guest: !!p.is_guest })) })} className="bg-[#CCFF00]/20 hover:bg-[#CCFF00]/30 text-[#CCFF00] border border-[#CCFF00]/40 text-[10px] uppercase font-bold h-7" data-testid={`planilla-btn-${r.id}-${idx}`}>
                              <ClipboardText size={12} className="mr-1" /> Planilla
                            </Button>
                          )}
                          {canEditSb && sbs.length > 0 && (
                            <Button size="sm" onClick={() => createNewRound(r.id, idx, Math.max(...sbs.map((s) => s.round || 1)))} className="bg-[#00A0FF] text-black hover:bg-[#0088DD] text-[10px] uppercase font-bold h-7" data-testid={`scoreboard-new-round-${r.id}-${idx}`}>
                              <Plus size={12} className="mr-1" /> Nueva ronda
                            </Button>
                          )}
                        </div>
                      </div>
                      {sbs.length === 0 ? (
                        <div className="space-y-2">
                          <div className="text-[10px] text-gray-600 italic">Sin pizarrones. Crea el primero con "Nueva reta".</div>
                          {canEditSb && (
                            <Button size="sm" onClick={() => createNewScoreboard(r.id, idx)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] text-[10px] uppercase font-bold h-7" data-testid={`scoreboard-new-${r.id}-${idx}`}>
                              <Plus size={12} className="mr-1" /> Nueva reta
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-2" data-testid={`scoreboard-rounds-${r.id}-${idx}`}>
                          {Object.entries(sbs.reduce((acc, s) => {
                            const rd = s.round || 1;
                            (acc[rd] = acc[rd] || []).push(s);
                            return acc;
                          }, {})).sort(([a], [b]) => Number(a) - Number(b)).map(([roundNum, roundSbs]) => (
                            <div key={roundNum} className="bg-white/[0.02] border border-white/5 rounded p-2" data-testid={`scoreboard-round-${r.id}-${idx}-${roundNum}`}>
                              <div className="flex items-center justify-between mb-1.5 flex-wrap gap-1">
                                <div className="flex items-center gap-2">
                                  <span className="px-1.5 py-0.5 bg-[#00A0FF]/15 text-[#00A0FF] text-[9px] tracking-[0.2em] uppercase font-bold rounded border border-[#00A0FF]/30">Ronda {roundNum}</span>
                                  <span className="text-[9px] text-gray-500">{roundSbs.length} reta{roundSbs.length !== 1 ? "s" : ""}</span>
                                </div>
                                {canEditSb && (
                                  <button onClick={() => createNewScoreboard(r.id, idx, Number(roundNum))} className="text-[9px] text-[#CCFF00] hover:underline uppercase tracking-wide" data-testid={`scoreboard-add-reta-${r.id}-${idx}-${roundNum}`}>
                                    + reta en R{roundNum}
                                  </button>
                                )}
                              </div>

                              {/* Jugadores de la ronda */}
                              <RoundRoster reta={r} courtIdx={idx} roundNum={Number(roundNum)} court={c} players={players} canEdit={canEditSb} onChanged={() => loadAll()} />

                              <div className="flex flex-wrap gap-1">
                                {roundSbs.sort((a, b) => (a.number || 0) - (b.number || 0)).map((s) => {
                                  const isActive = active?.id === s.id;
                                  const sa = s.score_a ?? 0;
                                  const sb = s.score_b ?? 0;
                                  return (
                                    <button
                                      key={s.id}
                                      onClick={() => setActiveSbFor(r.id, idx, isActive ? null : s.id)}
                                      className={`px-2 py-1 text-[10px] font-mono border rounded transition-colors ${isActive ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "bg-white/5 text-white border-white/15 hover:bg-white/10"}`}
                                      data-testid={`scoreboard-tab-${r.id}-${idx}-${roundNum}-${s.number}`}
                                    >
                                      R{roundNum}·#{s.number} <span className={isActive ? "text-black/70" : "text-gray-400"}>· {sa}-{sb}</span>
                                    </button>
                                  );
                                })}
                              </div>

                              {/* Pizarron activo: SOLO se renderiza si pertenece a ESTA ronda (debajo de sus tabs) */}
                              {active && (active.round || 1) === Number(roundNum) && (
                                <RetaScoreboard
                                  key={active.id}
                                  reta={r}
                                  courtIdx={idx}
                                  scoreboard={active}
                                  canEdit={canEditSb}
                                  onClose={() => setActiveSbFor(r.id, idx, null)}
                                  onDelete={canManageReta(r) ? () => deleteScoreboard(r.id, idx, active.id, active.number) : null}
                                  onSaved={() => loadAll()}
                                />
                              )}

                              {/* Cierre automatico de ronda */}
                              <RoundClosePanel scoreboards={roundSbs} roundNum={roundNum} testIdPrefix={`court-${r.id}-${idx}`} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Ranking de la cancha — sumatoria de puntos por jugador */}
                    <CourtRanking scoreboards={sbs} planilla={c.planilla} courtId={`${r.id}-${idx}`} testIdPrefix={`court-${r.id}-${idx}`} />
                  </div>
                  );
                })}
              </div>

              {/* Ranking GENERAL de toda la reta (suma de todas las canchas) */}
              <RetaGeneralRanking reta={r} testIdPrefix={`reta-general-ranking-${r.id}`} />
            </div>
          );
        })}
      </div>

      {/* LEGACY RETAS (read-only histórico) */}
      {legacyRetas.length > 0 && (
        <div className="space-y-3 pt-6 border-t border-white/5">
          <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500">Historial (formato antiguo 1vs1)</h2>
          {legacyRetas.map((r) => (
            <div key={r.id} className="card-tactical p-4 opacity-70" data-testid={`reta-legacy-${r.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-semibold text-white text-sm">{r.challenger_name}</span>
                <Sword size={14} className="text-gray-500" />
                <span className="font-semibold text-white text-sm">{r.challenged_name}</span>
                <Badge variant="outline" className={`badge-status ${statusColors[r.status] || ""}`}>{r.status}</Badge>
              </div>
              <div className="text-xs text-gray-500 mt-1">{r.date} {r.time} {r.score ? `· Score: ${r.score}` : ""}</div>
              {(isAdminOrDir || (r.created_by && r.created_by === user?.id)) && (
                <Button size="sm" variant="outline" onClick={() => handleDelete(r.id)} className="mt-2 border-red-500/30 text-red-400 hover:bg-red-500/10 text-[10px] uppercase h-7">
                  <Trash size={12} className="mr-1" /> Eliminar
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* CREATE DIALOG */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase">Nueva Reta</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <Label className="text-xs uppercase">Club</Label>
              <Select value={createForm.club_id} onValueChange={(v) => setCreateForm({ ...createForm, club_id: v, courts_text: "" })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10" data-testid="create-reta-club"><SelectValue placeholder="Selecciona club" /></SelectTrigger>
                <SelectContent>{clubs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs uppercase">Agenda (fecha)</Label><Input type="date" value={createForm.date} onChange={(e) => setCreateForm({ ...createForm, date: e.target.value })} required className="bg-[#0A0A0A] border-white/10" data-testid="create-reta-date" /></div>
              <div><Label className="text-xs uppercase">Horario</Label><Input type="time" value={createForm.time} onChange={(e) => setCreateForm({ ...createForm, time: e.target.value })} className="bg-[#0A0A0A] border-white/10" data-testid="create-reta-time" /></div>
            </div>
            <div>
              <Label className="text-xs uppercase">Canchas del club</Label>
              {(() => {
                const club = clubs.find((c) => c.id === createForm.club_id);
                const clubCourts = club?.court_names || [];
                const selected = (createForm.courts_text || "").split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
                const toggle = (name) => {
                  const next = selected.includes(name)
                    ? selected.filter((n) => n !== name)
                    : [...selected, name];
                  setCreateForm({ ...createForm, courts_text: next.join(", ") });
                };
                if (!createForm.club_id) {
                  return <p className="text-[11px] text-gray-500 italic py-2">Elige un club arriba para ver sus canchas</p>;
                }
                if (clubCourts.length === 0) {
                  return (
                    <>
                      <p className="text-[11px] text-amber-400 italic mb-1">Este club no tiene canchas registradas. Escribe nombres separados por coma:</p>
                      <Textarea value={createForm.courts_text} onChange={(e) => setCreateForm({ ...createForm, courts_text: e.target.value })} className="bg-[#0A0A0A] border-white/10 font-mono text-sm" rows={2} data-testid="create-reta-courts" />
                    </>
                  );
                }
                return (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-1" data-testid="create-reta-courts-grid">
                    {clubCourts.map((cn) => {
                      const on = selected.includes(cn);
                      return (
                        <button
                          key={cn}
                          type="button"
                          onClick={() => toggle(cn)}
                          className={`text-xs uppercase tracking-wider font-semibold border px-2 py-2 transition-colors ${on ? "bg-[#CCFF00]/15 border-[#CCFF00] text-[#CCFF00]" : "bg-[#0A0A0A] border-white/10 text-gray-400 hover:border-[#CCFF00]/30"}`}
                          data-testid={`create-reta-court-chip-${cn}`}
                        >
                          {on ? "✓ " : ""}{cn}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
              <p className="text-[10px] text-gray-500 mt-1">Cada cancha puede tener hasta 32 jugadores</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs uppercase">Cuota ($)</Label><Input type="number" min="0" step="0.01" value={createForm.entry_fee} onChange={(e) => setCreateForm({ ...createForm, entry_fee: e.target.value })} className="bg-[#0A0A0A] border-white/10" data-testid="create-reta-fee" /></div>
              <div>
                <Label className="text-xs uppercase">Categoria</Label>
                <Select value={createForm.category} onValueChange={(v) => setCreateForm({ ...createForm, category: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10"><SelectValue placeholder="Abierta" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Abierta</SelectItem>
                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase">Formato de juego</Label>
              <Select value={createForm.game_format} onValueChange={(v) => setCreateForm({ ...createForm, game_format: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10" data-testid="create-reta-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">{FORMAT_INFO.standard.label}</SelectItem>
                  <SelectItem value="short">{FORMAT_INFO.short.label}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase" htmlFor="pdf_blank_slots">Cupos extra por cancha en PDF</Label>
              <Input
                id="pdf_blank_slots"
                type="number"
                min={0}
                max={50}
                value={createForm.pdf_blank_slots_per_court}
                onChange={(e) => setCreateForm({ ...createForm, pdf_blank_slots_per_court: e.target.value })}
                className="bg-[#0A0A0A] border-white/10"
                data-testid="create-reta-pdf-blank-slots"
              />
              <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">Líneas en blanco que aparecerán en el PDF para anotar jugadores/partidos extra por cancha (0-50). Default: 10.</p>
            </div>
            <div>
              <Label className="text-xs uppercase">Descripcion</Label>
              <Textarea value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} className="bg-[#0A0A0A] border-white/10" rows={2} data-testid="create-reta-desc" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="border-white/10">Cancelar</Button>
              <Button type="submit" className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="submit-create-reta">Crear</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ASSIGN PLAYER DIALOG */}
      <Dialog open={!!assignDialog} onOpenChange={(v) => !v && setAssignDialog(null)}>
        <DialogContent className="bg-[#141414] border-white/10">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase">Asignar jugador · {assignDialog?.reta?.courts?.[assignDialog?.courtIdx]?.name}</DialogTitle></DialogHeader>

          {/* Mode tabs */}
          <div className="grid grid-cols-2 gap-1 p-1 bg-[#0A0A0A] border border-white/10 rounded mb-2">
            <button
              type="button"
              onClick={() => setAssignMode("manual")}
              className={`text-[10px] tracking-[0.15em] uppercase font-bold py-2 rounded transition-colors ${assignMode === "manual" ? "bg-[#CCFF00] text-black" : "text-gray-400 hover:text-white"}`}
              data-testid="assign-mode-manual"
            >
              ✍ Manual
            </button>
            <button
              type="button"
              onClick={() => setAssignMode("image")}
              className={`text-[10px] tracking-[0.15em] uppercase font-bold py-2 rounded transition-colors ${assignMode === "image" ? "bg-[#CCFF00] text-black" : "text-gray-400 hover:text-white"}`}
              data-testid="assign-mode-image"
            >
              📷 Desde Imagen (IA)
            </button>
          </div>

          {assignMode === "manual" ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase">Jugador registrado</Label>
                <SearchableUserSelect
                  value={assignForm.user_id}
                  onChange={(id, p) => setAssignForm({ user_id: id, name: p?.name || "", is_guest: false })}
                  options={players}
                  placeholder="Selecciona jugador..."
                  allowClear
                  testId="assign-reta-user"
                />
              </div>
              <div className="text-xs text-gray-500 text-center">— o —</div>
              <div>
                <Label className="text-xs uppercase">Invitado (nombre manual)</Label>
                <Input value={assignForm.user_id ? "" : assignForm.name} placeholder="Nombre del invitado" onChange={(e) => setAssignForm({ user_id: "", name: e.target.value, is_guest: true })} className="bg-[#0A0A0A] border-white/10" data-testid="assign-reta-guest" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAssignDialog(null)} className="border-white/10">Cancelar</Button>
                <Button onClick={handleAssign} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="submit-assign-reta">Asignar</Button>
              </DialogFooter>
            </div>
          ) : (
            <RosterImageAssign
              retaId={assignDialog?.reta?.id}
              courtIdx={assignDialog?.courtIdx}
              allPlayers={players}
              onAssigned={() => { setAssignDialog(null); loadRetas(); }}
              onCancel={() => setAssignDialog(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* EDIT RETA DIALOG */}
      <Dialog open={!!editDialog} onOpenChange={(v) => !v && setEditDialog(null)}>
        <DialogContent className="bg-[#141414] border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase">Editar reta</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs uppercase">Fecha</Label><Input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
              <div><Label className="text-xs uppercase">Horario</Label><Input type="time" value={editForm.time} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} className="bg-[#0A0A0A] border-white/10" /></div>
            </div>
            <div>
              <Label className="text-xs uppercase">Cuota ($)</Label>
              <Input type="number" min="0" step="0.01" value={editForm.entry_fee} onChange={(e) => setEditForm({ ...editForm, entry_fee: e.target.value })} className="bg-[#0A0A0A] border-white/10" />
            </div>
            <div>
              <Label className="text-xs uppercase">Formato</Label>
              <Select value={editForm.game_format} onValueChange={(v) => setEditForm({ ...editForm, game_format: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">{FORMAT_INFO.standard.label}</SelectItem>
                  <SelectItem value="short">{FORMAT_INFO.short.label}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase">Estado</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="abierto">Abierto</SelectItem>
                  <SelectItem value="en_curso">En curso</SelectItem>
                  <SelectItem value="finalizada">Finalizada</SelectItem>
                  <SelectItem value="cancelada">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase">Cupos extra por cancha en PDF</Label>
              <Input
                type="number"
                min={0}
                max={50}
                value={editForm.pdf_blank_slots_per_court}
                onChange={(e) => setEditForm({ ...editForm, pdf_blank_slots_per_court: e.target.value })}
                className="bg-[#0A0A0A] border-white/10"
                data-testid="edit-reta-pdf-blank-slots"
              />
              <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">Líneas en blanco para jugadores/partidos extra por cancha en el PDF (0-50).</p>
            </div>
            <div><Label className="text-xs uppercase">Descripcion</Label><Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="bg-[#0A0A0A] border-white/10" rows={2} /></div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog(null)} className="border-white/10">Cancelar</Button>
              <Button onClick={handleEdit} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase">Guardar</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Planilla por cancha — admin/directivo/profesor */}
      {planillaOpen && (
        <Dialog open={!!planillaOpen} onOpenChange={(o) => !o && setPlanillaOpen(null)}>
          <DialogContent className="max-w-6xl bg-[#0A0A0A] border-[#CCFF00]/30 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase">Planilla · {planillaOpen.courtName}</DialogTitle>
            </DialogHeader>
            <PlanillaPorCancha
              retaId={planillaOpen.retaId}
              courtIdx={planillaOpen.courtIdx}
              courtName={planillaOpen.courtName}
              registeredUsers={planillaOpen.registeredUsers}
              onClose={() => setPlanillaOpen(null)}
            />
          </DialogContent>
        </Dialog>
      )}
      {/* Bracket piramidal — admin/directivo/profesor */}
      {bracketOpen && (
        <Dialog open={!!bracketOpen} onOpenChange={(o) => !o && setBracketOpen(null)}>
          <DialogContent className="max-w-6xl bg-[#0A0A0A] border-amber-500/30 max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase flex items-center gap-2">
                <Trophy size={20} className="text-amber-400" /> Bracket Piramidal · {bracketOpen.retaName}
              </DialogTitle>
            </DialogHeader>
            <RetaBracket
              retaId={bracketOpen.retaId}
              canEdit={canSeePlanilla}
              onClose={() => setBracketOpen(null)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
